import { useTranslation } from 'react-i18next';
import type { MyProfileResponse } from '../types/myProfile';

interface IdentityCardProps {
  profile: MyProfileResponse;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function IdentityCard({ profile }: IdentityCardProps) {
  const { t } = useTranslation();
  const initials = getInitials(profile.displayName);

  const roleLabel = t(`myProfile.roleMap.${profile.role}`, { defaultValue: profile.role });
  const localeLabel = profile.preferredLocale === 'ko' ? t('myProfile.identity.localeLabelKo') : 'English';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-6)',
        padding: 'var(--spacing-6)',
        marginBottom: 'var(--spacing-6)',
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-lg)',
        flexWrap: 'wrap',
      }}
      aria-label={t('myProfile.identity.ariaLabel')}
    >
      {/* Avatar */}
      <div
        aria-hidden="true"
        style={{
          width: 96,
          height: 96,
          borderRadius: 'var(--radius-full)',
          background: profile.avatarDownloadUrl
            ? 'var(--color-surface-elevated)'
            : 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary))',
          color: 'var(--color-text-inverse)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 40,
          fontWeight: 800,
          flexShrink: 0,
          border: '3px solid color-mix(in srgb, var(--color-brand-primary) 50%, var(--color-surface-elevated))',
          overflow: 'hidden',
        }}
      >
        {profile.avatarDownloadUrl ? (
          <img
            src={profile.avatarDownloadUrl}
            alt={t('myProfile.identity.avatarAlt', { name: profile.displayName })}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initials
        )}
      </div>

      {/* Meta info */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
            }}
          >
            {profile.displayName}
          </span>
          {/* "자기 자신" 배지 */}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              padding: '2px var(--spacing-3)',
              borderRadius: 'var(--radius-full)',
              background: 'color-mix(in srgb, var(--color-info) 18%, transparent)',
              color: 'var(--color-info)',
              border: '1px solid var(--color-info)',
            }}
          >
            {t('myProfile.page.selfBadge')}
          </span>
        </div>

        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          {profile.email}
        </div>

        {/* Chip tags */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <Chip label={t('myProfile.identity.roleLabel')} value={roleLabel} />
          <Chip
            label={t('myProfile.identity.localeLabel')}
            value={localeLabel}
          />
          <Chip label={t('myProfile.identity.timezoneLabel')} value={profile.preferredTimezone} />
          {profile.lastLoginAt && (
            <Chip label={t('myProfile.identity.lastLoginLabel')} value={formatDateTime(profile.lastLoginAt)} />
          )}
          {profile.passwordChangedAt && (
            <Chip label={t('myProfile.identity.passwordChangedAt')} value={formatDateTime(profile.passwordChangedAt)} />
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        padding: '2px var(--spacing-3)',
        borderRadius: 'var(--radius-full)',
        border: '1px solid var(--color-surface-border)',
        color: 'var(--color-text-secondary)',
        background: 'var(--color-surface-elevated)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <span style={{ color: 'var(--color-text-muted)' }}>{label}:</span>
      {value}
    </span>
  );
}
